(() => {
  const config = window.THMarketing || {};
  const state = {
    cart: {
      currency: config.cart?.currency || config.currency || '',
      items: Array.isArray(config.cart?.items) ? config.cart.items.map((item) => ({ ...item })) : [],
    },
  };
  const seenEvents = new Set();

  const stripEmpty = (value) => {
    if (Array.isArray(value)) {
      return value.filter((item) => item !== undefined && item !== null && item !== '');
    }

    return Object.fromEntries(
      Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '')
    );
  };

  const roundPrice = (value) => Number((Number(value) || 0).toFixed(2));
  const centsToMoney = (value) => roundPrice(Number(value || 0) / 100);
  const currentCurrency = () => state.cart.currency || config.currency || '';

  const generateEventId = (prefix) => {
    if (window.crypto?.randomUUID) return `${prefix}.${window.crypto.randomUUID()}`;
    return `${prefix}.${Date.now()}.${Math.random().toString(16).slice(2)}`;
  };

  const totalValue = (items) =>
    roundPrice(
      (items || []).reduce((sum, item) => {
        return sum + (Number(item.price) || 0) * (Number(item.quantity) || 1);
      }, 0)
    );

  const safeParse = (value) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const mapLiquidItem = (item = {}) =>
    stripEmpty({
      item_id: item.item_id || item.item_variant_id || item.variant_id || item.product_id,
      item_name: item.item_name,
      item_brand: item.item_brand,
      item_category: item.item_category,
      item_variant: item.item_variant && item.item_variant !== 'Default Title' ? item.item_variant : undefined,
      item_variant_id: item.item_variant_id || item.variant_id,
      price: roundPrice(item.price),
      quantity: Number(item.quantity) || 1,
      item_url: item.item_url,
      google_business_vertical: 'retail',
    });

  const mapAjaxItem = (item = {}) =>
    stripEmpty({
      item_id: item.sku || item.variant_id || item.id || item.product_id,
      item_name: item.product_title || item.title,
      item_brand: item.vendor,
      item_category: item.product_type,
      item_variant: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : undefined,
      item_variant_id: item.variant_id || item.id,
      price: centsToMoney(item.final_price ?? item.price ?? item.original_price),
      quantity: Number(item.quantity) || 1,
      item_url: item.url,
      google_business_vertical: 'retail',
    });

  const buildEcommercePayload = (items, extra = {}) => {
    const normalizedItems = (items || []).map(mapLiquidItem).filter((item) => item.item_id || item.item_name);
    if (!normalizedItems.length) return null;

    return stripEmpty({
      currency: extra.currency || currentCurrency(),
      value: extra.value !== undefined ? roundPrice(extra.value) : totalValue(normalizedItems),
      item_list_id: extra.item_list_id,
      item_list_name: extra.item_list_name,
      items: normalizedItems,
    });
  };

  const updateCartState = (cartData) => {
    state.cart = {
      currency: cartData.currency || currentCurrency(),
      items: Array.isArray(cartData.items) ? cartData.items.map(mapAjaxItem) : [],
    };
  };

  const mergeCartItem = (item) => {
    if (!item?.item_id && !item?.item_variant_id && !item?.item_name) return;

    const key = String(item.item_variant_id || item.item_id);
    const existingItem = state.cart.items.find(
      (cartItem) => String(cartItem.item_variant_id || cartItem.item_id) === key
    );

    if (existingItem) {
      existingItem.quantity = (Number(existingItem.quantity) || 0) + (Number(item.quantity) || 1);
      existingItem.price = item.price ?? existingItem.price;
      existingItem.item_url = item.item_url ?? existingItem.item_url;
      return;
    }

    state.cart.items.push(item);
  };

  const fetchCartState = async () => {
    if (!config.routes?.cartJsonUrl) return;

    try {
      const response = await fetch(config.routes.cartJsonUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cartData = await response.json();
      updateCartState(cartData);
    } catch (error) {
      // Ignore measurement refresh failures so storefront UX is never blocked.
    }
  };

  const pushDataLayerEvent = (eventName, payload, eventId) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(
      stripEmpty({
        event: eventName,
        event_id: eventId,
        page_type: config.pageType,
        template_name: config.templateName,
        ecommerce: payload,
      })
    );
  };

  const sendGoogleEvent = (eventName, payload) => {
    if (!config.ids?.ga4 && !config.ids?.googleAds) return;
    if (typeof gtag !== 'function') return;
    gtag('event', eventName, payload);
  };

  const sendMetaEvent = (eventName, payload, eventId) => {
    if (!config.ids?.metaPixel) return;
    if (typeof fbq !== 'function') return;

    const metaEventMap = {
      view_item: 'ViewContent',
      add_to_cart: 'AddToCart',
      begin_checkout: 'InitiateCheckout',
      search: 'Search',
    };

    const metaEventName = metaEventMap[eventName];
    if (!metaEventName) return;

    if (eventName === 'search') {
      fbq(
        'track',
        metaEventName,
        stripEmpty({
          search_string: payload.search_term,
        }),
        { eventID: eventId }
      );
      return;
    }

    const items = payload.items || [];
    const metaPayload = stripEmpty({
      content_type: items.length > 1 ? 'product_group' : 'product',
      content_ids: items.map((item) => String(item.item_variant_id || item.item_id)).filter(Boolean),
      contents: items
        .map((item) =>
          stripEmpty({
            id: String(item.item_variant_id || item.item_id || ''),
            quantity: Number(item.quantity) || 1,
            item_price: item.price,
          })
        )
        .filter((item) => item.id),
      content_name: items.length === 1 ? items[0].item_name : undefined,
      value: payload.value,
      currency: payload.currency,
      num_items: items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0),
    });

    fbq('track', metaEventName, metaPayload, { eventID: eventId });
  };

  const emitEcommerceEvent = (eventName, payload, options = {}) => {
    if (!payload?.items?.length) return;

    if (options.onceKey && seenEvents.has(options.onceKey)) return;
    if (options.onceKey) seenEvents.add(options.onceKey);

    const eventId = generateEventId(eventName);
    pushDataLayerEvent(eventName, payload, eventId);
    sendGoogleEvent(eventName, payload);
    sendMetaEvent(eventName, payload, eventId);
  };

  const emitSimpleEvent = (eventName, params = {}, options = {}) => {
    if (options.onceKey && seenEvents.has(options.onceKey)) return;
    if (options.onceKey) seenEvents.add(options.onceKey);

    const eventId = generateEventId(eventName);
    const cleanParams = stripEmpty(params);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      stripEmpty({
        event: eventName,
        event_id: eventId,
        page_type: config.pageType,
        template_name: config.templateName,
        ...cleanParams,
      })
    );

    sendGoogleEvent(eventName, cleanParams);
    sendMetaEvent(eventName, cleanParams, eventId);
  };

  const getCurrentProductItem = () => {
    const script = document.querySelector('.th-product-tracking-data');
    if (!script) return null;

    const item = safeParse(script.textContent);
    return item ? mapLiquidItem(item) : null;
  };

  const getListItems = () => {
    const listName = document.title;
    const listId = config.pageType || config.templateName || 'listing';

    return Array.from(document.querySelectorAll('.product-card-wrapper[data-th-item]'))
      .map((wrapper, index) => {
        const item = safeParse(wrapper.dataset.thItem || '');
        if (!item) return null;

        return mapLiquidItem({
          ...item,
          item_list_id: listId,
          item_list_name: listName,
          index,
        });
      })
      .filter(Boolean);
  };

  const trackProductView = () => {
    const item = getCurrentProductItem();
    if (!item) return;

    emitEcommerceEvent(
      'view_item',
      buildEcommercePayload([item], {
        currency: item.currency || currentCurrency(),
        value: Number(item.price) || 0,
      }),
      { onceKey: `view_item:${item.item_variant_id || item.item_id}` }
    );
  };

  const trackViewCart = () => {
    if (config.pageType !== 'cart' || !state.cart.items.length) return;

    emitEcommerceEvent('view_cart', buildEcommercePayload(state.cart.items), {
      onceKey: `view_cart:${window.location.pathname}`,
    });
  };

  const trackSearch = () => {
    if (!config.searchTerm) return;

    emitSimpleEvent(
      'search',
      {
        search_term: config.searchTerm,
      },
      { onceKey: `search:${config.searchTerm}` }
    );
  };

  const trackItemListView = () => {
    const items = getListItems();
    if (!items.length) return;

    emitEcommerceEvent(
      'view_item_list',
      buildEcommercePayload(items, {
        item_list_id: config.pageType || config.templateName || 'listing',
        item_list_name: document.title,
      }),
      { onceKey: `view_item_list:${window.location.pathname}` }
    );
  };

  const initSelectItemTracking = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('.product-card-wrapper a[href]');
      if (!link) return;

      const wrapper = link.closest('.product-card-wrapper[data-th-item]');
      if (!wrapper) return;

      const item = safeParse(wrapper.dataset.thItem || '');
      if (!item) return;

      const payload = buildEcommercePayload(
        [
          {
            ...item,
            item_list_id: config.pageType || config.templateName || 'listing',
            item_list_name: document.title,
          },
        ],
        {
          item_list_id: config.pageType || config.templateName || 'listing',
          item_list_name: document.title,
        }
      );

      if (!payload) return;

      const eventId = generateEventId('select_item');
      pushDataLayerEvent('select_item', payload, eventId);
      sendGoogleEvent('select_item', payload);
    });
  };

  const initCartEventTracking = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event) return;

      if (event.source === 'product-form' && event.cartData) {
        const addedItem = mapAjaxItem(event.cartData);
        mergeCartItem(addedItem);

        emitEcommerceEvent(
          'add_to_cart',
          buildEcommercePayload([addedItem], {
            value: (Number(addedItem.price) || 0) * (Number(addedItem.quantity) || 1),
          })
        );

        fetchCartState();
        return;
      }

      if (event.cartData?.items) {
        updateCartState(event.cartData);
      }
    });

    if (typeof PUB_SUB_EVENTS.variantChange !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        window.setTimeout(trackProductView, 0);
      });
    }
  };

  const initCheckoutTracking = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const checkoutButton =
        form.querySelector('[name="checkout"]') ||
        (form.id && ['cart', 'CartDrawer-Form', 'cart-notification-form'].includes(form.id));

      if (!checkoutButton) return;

      emitEcommerceEvent('begin_checkout', buildEcommercePayload(state.cart.items));
    });
  };

  trackSearch();
  trackProductView();
  trackViewCart();
  trackItemListView();
  initSelectItemTracking();
  initCartEventTracking();
  initCheckoutTracking();
})();
